// src/components/SignoffPanel.js
//
// RULING 230 (2026-09-25). Each officer creates his OWN signature sign-off - Bob: "and signature
// sign-off". Written to users/{uid}.signoff by that person alone (self-scope; firestore.rules). The
// typed sign-off ("/s/ Christian Eckert, General Chairman, GO-342") is the safe default; an uploaded
// signature IMAGE is an OPTIONAL convenience, stored scoped to him alone at signatures/{uid}
// (storage.rules) and NEVER auto-applied - it is affixed only when he signs off on a specific letter
// (ruling 231, DisciplineLettersPanel / LetterGroupPanel).
//
// Every rule of what-shape is src\data\signoff.js (pure, tested); this draws, writes and uploads.
import React, { useEffect, useMemo, useState } from 'react';
import { db, storage } from '../firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { signoffRecord, typedSignoffLine, hasSignoff, SIGNATURE_IMAGE } from '../data/signoff';
import RulingRef from './RulingRef';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

export default function SignoffPanel({ user }) {
  const uid = user && user.uid;
  const [record, setRecord] = useState(undefined);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [busyImg, setBusyImg] = useState(false);
  // Forensic audit 2026-10-16: true once he types and until he saves. While it is set nothing from the server
  // overwrites his form.
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!uid) return undefined;
    setRecord(undefined); setDirty(false);
    const unsub = onSnapshot(doc(db, 'users', uid),
      s => { setRecord(s.exists() ? (s.data().signoff || null) : null); setErr(''); },
      // Forensic audit 2026-10-16: a failed read is NOT "no sign-off" - the record is left unknown, so the panel never
      // says "not set" and never seeds a default form to save over the sign-off he has.
      e => { setErr('Could not read your record: ' + (e && e.code ? e.code : e.message) + '. Whether you have a sign-off on file is unknown until it can be read.'); });
    return () => unsub();
  }, [uid]);

  // Seed the form from the stored sign-off, defaulting the name/title/committee to this account's
  // own identity - his own name, never invented.
  // Forensic audit 2026-10-16: `record` is a new object on EVERY snapshot of users/{uid}, and that document is written
  // by much else (profile, notifications state, sign-in), so each such write used to re-seed the form and wipe his
  // unsaved edits. The form now follows the STORED VALUES (a string of them) and never while he has unsaved edits.
  const storedKey = record === undefined ? undefined : JSON.stringify({
    typedName: (record && record.typedName) || (user && user.name) || '',
    typedTitle: (record && record.typedTitle) || (user && user.roleDisplay) || '',
    typedCommittee: (record && record.typedCommittee) || (user && user.committee) || '',
  });
  useEffect(() => {
    if (storedKey === undefined || dirty) return;
    setForm(JSON.parse(storedKey));
  }, [storedKey, dirty]);

  // Resolve the signature image, if one is on file, for the preview.
  useEffect(() => {
    let dead = false;
    (async () => {
      if (record && record.imagePath) {
        try { const u = await getDownloadURL(ref(storage, record.imagePath)); if (!dead) setImgUrl(u); }
        catch { if (!dead) setImgUrl(''); }
      } else if (!dead) setImgUrl('');
    })();
    return () => { dead = true; };
  }, [record && record.imagePath]);

  const line = useMemo(() => form ? typedSignoffLine(form) : '', [form]);
  if (!uid) return null;

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); setSaved(false); setErrors([]); };

  const saveTyped = async (extra) => {
    if (record === undefined) { setErrors(['Not saved: your record has not been read, so nothing is written over it.']); return false; }   // Forensic audit 2026-10-16
    setErrors([]); setSaved(false);
    const fields = {
      typedName: form.typedName, typedTitle: form.typedTitle, typedCommittee: form.typedCommittee,
      imagePath: (extra && 'imagePath' in extra) ? extra.imagePath : (record && record.imagePath) || null,
    };
    const r = signoffRecord(user, fields);   // the guard: validates, self-scopes, shapes the sign-off (ruling 230)
    if (!r.ok) { setErrors(r.errors); return false; }
    setSaving(true);
    // signoffRecord() is on the write line so the guard is visible where the write happens (PRF-1); the
    // recordedAt from the validated result is reused so the write is deterministic, not a second clock.
    try { await setDoc(doc(db, 'users', uid), signoffRecord(user, fields, r.patch.signoff.recordedAt).patch, { merge: true }); setSaved(true); setDirty(false); return true; }
    catch (e) { setErrors(['Not saved: ' + (e && e.code ? e.code : e.message)]); return false; }
    finally { setSaving(false); }
  };

  const onImage = async (file) => {
    setErrors([]);
    if (!file) return;
    if (!SIGNATURE_IMAGE.CONTENT_TYPES.includes(file.type)) { setErrors(['A signature image must be PNG or JPEG.']); return; }
    if (file.size >= SIGNATURE_IMAGE.MAX_BYTES) { setErrors(['A signature image must be under 2 MB.']); return; }
    if (!form || !String(form.typedName).trim()) { setErrors(['Enter and save your typed sign-off first - the typed name is the sign-off; the image only accompanies it.']); return; }
    setBusyImg(true);
    try {
      const ext = file.type === 'image/png' ? 'png' : 'jpg';
      const path = SIGNATURE_IMAGE.pathFor(uid, 'signature.' + ext);
      await uploadBytes(ref(storage, path), file, { contentType: file.type });
      await saveTyped({ imagePath: path });   // record the pointer only after the bytes are up
    } catch (e) { setErrors(['Upload failed: ' + (e && e.code ? e.code : e.message)]); }
    setBusyImg(false);
  };

  const removeImage = async () => {
    if (!record || !record.imagePath) return;
    setBusyImg(true); setErrors([]);
    try { await deleteObject(ref(storage, record.imagePath)); } catch { /* may already be gone; clear the pointer regardless */ }
    await saveTyped({ imagePath: null });
    setBusyImg(false);
  };

  return (
    <div style={{ border: '1px solid ' + (hasSignoff(record) ? C.line : C.accent), background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid ' + C.line }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Your signature sign-off</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>Your own sign-off, used only when YOU sign off on a letter. Nothing is signed automatically, and nobody sets this but you. The typed sign-off is all you need; a signature image is optional.<RulingRef user={user} n={237} why="Each officer creates his own signature sign-off; nobody else sets it for him." /></div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: hasSignoff(record) ? C.ok : C.accent, whiteSpace: 'nowrap' }}>{record === undefined ? (err ? 'could not read' : '…') : hasSignoff(record) ? 'set' : 'not set'}</div>
      </div>
      <div style={{ padding: '10px 14px 12px', fontSize: 12, color: C.dim }}>
        {err ? <div style={{ color: C.bad, marginBottom: 8 }}>&#9888; {err}</div> : null}
        {form ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="form-group" style={{ gridColumn: '1 / span 2' }}>
                <label className="form-label">Name it signs with (required)</label>
                <input aria-label="Name it signs with (required)" className="form-input" value={form.typedName} placeholder="Christian Eckert" onChange={e => set('typedName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Title (optional)</label>
                <input aria-label="Title (optional)" className="form-input" value={form.typedTitle} placeholder="General Chairman" onChange={e => set('typedTitle', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Committee (optional)</label>
                <input aria-label="Committee (optional)" className="form-input" value={form.typedCommittee} placeholder="GO-342" onChange={e => set('typedCommittee', e.target.value)} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: C.dim, marginBottom: 4 }}>Your typed sign-off will read:</div>
              <pre style={{ whiteSpace: 'pre-wrap', background: '#07101C', border: '1px solid ' + C.line, borderRadius: 6, padding: 10, color: C.text, minHeight: 18 }}>{line || '(enter a name)'}</pre>
            </div>
            {errors.length ? <div style={{ color: C.bad, marginBottom: 6 }}>{errors.map((e, i) => <div key={i}>&#9888; {e}</div>)}</div> : null}
            {dirty ? <span style={{ marginRight: 10, color: C.accent }}>Unsaved changes.</span> : null}
            <button className="btn btn-primary btn-sm" disabled={saving || !!err || record === undefined || !String(form.typedName).trim()} onClick={() => saveTyped()}>{saving ? 'Saving…' : 'Save typed sign-off'}</button>
            {saved ? <span style={{ marginLeft: 10, color: C.ok }}>Saved.</span> : null}

            <div style={{ borderTop: '1px solid ' + C.line, marginTop: 12, paddingTop: 10 }}>
              <div style={{ color: C.text, fontWeight: 700, marginBottom: 4 }}>Signature image (optional)</div>
              <div style={{ color: C.dim, marginBottom: 8 }}>PNG or JPEG, under 2 MB. Held for you alone. It is affixed only when you sign off on a specific letter - never automatically, and it does not replace the typed sign-off.</div>
              {imgUrl ? (
                <div style={{ marginBottom: 8 }}>
                  <img src={imgUrl} alt="Your signature" style={{ maxHeight: 60, background: '#fff', padding: 4, borderRadius: 4 }} />
                  <div><button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} disabled={busyImg} onClick={removeImage}>{busyImg ? '…' : 'Remove image'}</button></div>
                </div>
              ) : <div style={{ color: C.dim, marginBottom: 8 }}>No signature image on file.</div>}
              <input aria-label="Attach your signature image (PNG or JPEG)" type="file" accept="image/png,image/jpeg" disabled={busyImg} onChange={e => onImage(e.target.files && e.target.files[0])} />
            </div>
          </div>
        ) : (err ? null : <div>…</div>)}
      </div>
    </div>
  );
}
